import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Phone, Mail, MapPin, Facebook, Instagram, Youtube, Linkedin, Music2 } from "lucide-react";
import HesnLogo from '../../assets/logo.png';
import { useSettings } from '../api/hooks/useSettings';
import { useProjects } from '../api/hooks/useProjects';
import { useNewsletter } from '../api/hooks/useNewsletter';
import { useState } from 'react';

export const Footer = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';
    const { data: settings } = useSettings();
    const { data: projects, isLoading: isLoadingProjects } = useProjects();

    // Newsletter Logic
    const [email, setEmail] = useState('');
    const { mutate: subscribe, isPending } = useNewsletter();

    const handleSubscribe = () => {
        if (!email) return;
        subscribe(email, {
            onSuccess: () => setEmail('')
        });
    };

    const footerDescription = isRTL ? settings?.footerDescriptionAr : settings?.footerDescriptionEn;
    const address = isRTL ? settings?.addressAr : settings?.addressEn;

    return (
        <footer className="bg-secondary text-white py-16 sm:py-24 mt-12 sm:mt-20 border-t border-slate-800">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">
                <div className="grid grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-8">

                    {/* Brand Column (Span 4) */}
                    <div className="col-span-2 lg:col-span-4 flex flex-col items-start">
                        <img
                            src={HesnLogo}
                            alt="HESN Development"
                            className="h-10 sm:h-12 w-auto mb-8 brightness-0 invert opacity-90"
                        />
                        <div
                            className="text-slate-400 text-sm leading-relaxed max-w-xs mb-8 prose prose-invert prose-sm"
                            dangerouslySetInnerHTML={{ __html: footerDescription || t('footer.tagline') }}
                        />

                        {/* Newsletter */}
                        <div className="w-full max-w-xs">
                            <h5 className="text-xs font-bold uppercase tracking-widest text-slate-200 mb-4 font-display">
                                {isRTL ? 'اشترك في نشرتنا' : 'Subscribe to our newsletter'}
                            </h5>
                            <div className="flex gap-2">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder={isRTL ? 'بريدك الإلكتروني' : 'Your email'}
                                    className="bg-white/5 border border-white/10 rounded px-4 py-2 text-sm w-full focus:outline-none focus:border-white/30 text-white placeholder-slate-500 transition-colors"
                                    onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
                                />
                                <button
                                    onClick={handleSubscribe}
                                    disabled={isPending}
                                    className="bg-white text-secondary px-4 py-2 rounded text-sm font-bold hover:bg-slate-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isPending ? '...' : (isRTL ? '→' : '→')}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Links Column 1: Projects (Span 2) */}
                    <div className="col-span-1 lg:col-span-2 lg:col-start-6">
                        <h4 className={clsx(
                            "text-xs font-bold uppercase tracking-widest mb-6 text-slate-200",
                            isRTL && "tracking-normal normal-case font-display"
                        )}>
                            {t('footer.projects')}
                        </h4>
                        <ul className="space-y-4 text-slate-400 text-sm">
                            {/* Dynamic Projects */}
                            {projects?.slice(0, 5).map((project) => (
                                <li key={project.id}>
                                    <Link to={`/projects/${project.slug}`} className="hover:text-white transition-colors duration-300 block">
                                        {project.title}
                                    </Link>
                                </li>
                            ))}
                            {!projects?.length && !isLoadingProjects && (
                                <li className="text-slate-500 italic">No projects found</li>
                            )}


                        </ul>
                    </div>

                    {/* Links Column 2: Company (Span 2) */}
                    <div className="col-span-1 lg:col-span-2">
                        <h4 className={clsx(
                            "text-xs font-bold uppercase tracking-widest mb-6 text-slate-200",
                            isRTL && "tracking-normal normal-case font-display"
                        )}>
                            {t('footer.company')}
                        </h4>
                        <ul className="space-y-4 text-slate-400 text-sm">
                            <li><Link to="/pages/about-us" className="hover:text-white transition-colors duration-300 block">{t('footer.aboutUs')}</Link></li>
                            <li><Link to="/pages/careers" className="hover:text-white transition-colors duration-300 block">{t('footer.careers')}</Link></li>
                            <li><Link to="/pages/contact-us" className="hover:text-white transition-colors duration-300 block">{t('footer.contactUs')}</Link></li>
                        </ul>
                    </div>

                    {/* Links Column 3: Connect (Span 2) */}
                    <div className="col-span-2 md:col-span-1 lg:col-span-2">
                        <h4 className={clsx(
                            "text-xs font-bold uppercase tracking-widest mb-6 text-slate-200",
                            isRTL && "tracking-normal normal-case font-display"
                        )}>
                            {t('footer.connect')}
                        </h4>

                        {/* Contact Info */}
                        <ul className="space-y-4 text-slate-400 text-sm mb-6">
                            {settings?.phone && (
                                <li className="flex items-center gap-2">
                                    <Phone size={14} />
                                    <a href={`tel:${settings.phone}`} className="hover:text-white transition-colors">{settings.phone}</a>
                                </li>
                            )}
                            {settings?.email && (
                                <li className="flex items-center gap-2">
                                    <Mail size={14} />
                                    <a href={`mailto:${settings.email}`} className="hover:text-white transition-colors">{settings.email}</a>
                                </li>
                            )}
                            {address && (
                                <li className="flex items-start gap-2">
                                    <MapPin size={14} className="mt-1 flex-shrink-0" />
                                    <span>{address}</span>
                                </li>
                            )}
                        </ul>

                        {/* Social Icons */}
                        <div className="flex flex-wrap gap-3">
                            {settings?.whatsapp && (
                                <a href={`https://wa.me/${settings.whatsapp.replace('+', '')}`} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-green-400 transition-colors">
                                    <Phone size={16} />
                                </a>
                            )}
                            {settings?.facebook && (
                                <a href={settings.facebook} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-blue-500 transition-colors">
                                    <Facebook size={16} />
                                </a>
                            )}
                            {settings?.instagram && (
                                <a href={settings.instagram} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-pink-500 transition-colors">
                                    <Instagram size={16} />
                                </a>
                            )}
                            {settings?.youtube && (
                                <a href={settings.youtube} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-red-500 transition-colors">
                                    <Youtube size={16} />
                                </a>
                            )}
                            {settings?.tiktok && (
                                <a href={settings.tiktok} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-pink-500 transition-colors">
                                    <Music2 size={16} />
                                </a>
                            )}
                            {settings?.linkedIn && (
                                <a href={settings.linkedIn} target="_blank" rel="noopener noreferrer" className="p-2 bg-white/5 rounded-full hover:bg-white/10 hover:text-blue-400 transition-colors">
                                    <Linkedin size={16} />
                                </a>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-slate-500">
                    <p>{t('footer.copyright')}</p>
                    <div className="flex gap-6">
                        <Link to="/pages/privacy-policy" className="hover:text-slate-300 transition-colors">Privacy Policy</Link>
                        <Link to="/pages/terms-conditions" className="hover:text-slate-300 transition-colors">Terms of Service</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

